import { useState, useEffect } from 'react';
import { Vulnerability } from '../types/vulnerability';
import { dataLoader } from '../utils/dataLoader';

const REMOTE_URL = 'https://raw.githubusercontent.com/chanduusc/Ui-Demo-Data/main/ui_demo.json';
const BUILD_DATA_SOURCE = import.meta.env.VITE_DATA_SOURCE ?? 'runtime';
export type DataSource = 'remote' | 'remote-prebuilt' | 'local-sample' | 'mock';

/**
 * React hook that loads vulnerability data, preferring the remote source and
 * falling back to the bundled copy or generated mock data.
 */
export function useVulnerabilityData(localPath = '/ui_demo.json') {
  const [data, setData] = useState<Vulnerability[] | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [source, setSource] = useState<DataSource>('remote');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(undefined);

      const dataLoaderInstance = dataLoader;

      if (BUILD_DATA_SOURCE === 'prebuilt') {
        const resolvedLocalPath = `${import.meta.env.BASE_URL}${localPath.replace(/^\//, '')}`;
        try {
          const bundledData = await dataLoaderInstance.loadData(resolvedLocalPath);
          if (!cancelled) {
            setData(bundledData);
            setSource('remote-prebuilt');
            setIsLoading(false);
          }
          return;
        } catch (prebuiltError) {
          if (import.meta.env.DEV) {
            console.warn(`Prebuilt data fetch failed (${resolvedLocalPath}), retrying runtime sources:`, prebuiltError);
          }
        }
      }

      try {
        const remoteData = await dataLoaderInstance.loadData(REMOTE_URL);
        if (!cancelled) {
          setData(remoteData);
          setSource('remote');
          setError(undefined);
          setIsLoading(false);
        }
        return;
      } catch (remoteError) {
        if (import.meta.env.DEV) {
          console.warn('Remote data fetch failed, falling back to local file:', remoteError);
        }
      }

      const resolvedLocalPath = `${import.meta.env.BASE_URL}${localPath.replace(/^\//, '')}`;
      const resolvedSamplePath = `${import.meta.env.BASE_URL}ui_demo.sample.json`;

      try {
        const localData = await dataLoaderInstance.loadData(resolvedLocalPath);
        if (!cancelled) {
          setData(localData);
          setSource('remote-prebuilt');
          setError(undefined);
          setIsLoading(false);
        }
        return;
      } catch (localError) {
        if (import.meta.env.DEV) {
          console.warn(`Local data fetch failed (${resolvedLocalPath}), falling back to sample dataset:`, localError);
        }
      }

      try {
        const sampleData = await dataLoaderInstance.loadData(resolvedSamplePath);
        if (!cancelled) {
          setData(sampleData);
          setSource('local-sample');
          setError(undefined);
          setIsLoading(false);
        }
        return;
      } catch (sampleError) {
        if (import.meta.env.DEV) {
          console.warn(`Sample data fetch failed (${resolvedSamplePath}), falling back to mock data:`, sampleError);
        }
      }

      if (!cancelled) {
        const mockData = dataLoaderInstance.generateMockData();
        setData(mockData);
        setSource('mock');
        setError(undefined);
        setIsLoading(false);
      }
    }

    load().catch((err) => {
      if (!cancelled) {
        setError(err);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [localPath]);

  return { data, error, isLoading, source } as const;
}

